import { $generateNodesFromDOM } from "@lexical/html"
import { $isLinkNode, LinkNode } from "@lexical/link"
import { $createListNode } from "@lexical/list"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $createHeadingNode } from "@lexical/rich-text"
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  BLUR_COMMAND,
  LexicalEditor,
  RootNode,
  TextNode,
} from "lexical"
import React, { useCallback, useEffect } from "react"
import RgptParagraphNode from "../CustomNodes/ParagraphNode"
import { InsertMention } from "lexical-beautiful-mentions/mention-commands"
import { useBeautifulMentions } from "lexical-beautiful-mentions"
import { $isInlineImageNode } from "./InlineImagePlugin/InlineImageNode"
import { useTextEditorControllerContext } from "../TextEditor.context"

const TYPING_DELAY_MS = 5

type MakeLinkEditableCallback = (linkNode: LinkNode, shouldAutoOpenEditor?: boolean) => void

interface Props {
  initialText?: string
  imitateTyping?: boolean
  makeLinkEditable: MakeLinkEditableCallback
  enableVariables: boolean

  onBlur?: () => void
  setIsTyping: (v: boolean) => void
}

const getParentNode = (rawElement: Element) => {
  switch (rawElement.tagName) {
    case "H2":
      return $createHeadingNode("h2")

    case "H1":
      return $createHeadingNode("h1")

    case "UL":
      return $createListNode("bullet")
    case "OL":
      return $createListNode("number")

    default:
      return new RgptParagraphNode({ style: rawElement.getAttribute("style") || "" })
  }
}

/**
 * We only care about user-driven blur, we want to ignore ones we do programmatically for internal purposes, e.g. ones triggered when inserting variables
 */
let shouldCallBlurCallback = true

/**
 * Turn all text nodes that contain variable-like text into real variables
 */
function insertVariables(root: RootNode, editor: LexicalEditor, insertMention: (options: InsertMention) => boolean) {
  shouldCallBlurCallback = false

  // Make sure the editor is properly focused
  editor.update(() => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) editor.focus()
  })

  editor.update(() => {
    const textNodes = root.getAllTextNodes()
    for (const node of textNodes) {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const isVariable = node.__text.match(/{{.+}}/)

      if (isVariable) {
        const valueText = node.__text.slice(2)
        selection.setTextNodeRange(node, 0, node, node.__text.length)
        selection.deleteWord(true)
        insertMention({ trigger: "{{", value: valueText })
      }
    }

    shouldCallBlurCallback = true
  })
}

function fixCustomUnsubscribeLinks(editor: LexicalEditor, unsubscribeLinkNodeKeys: string[]) {
  const unsubscribeLinks = unsubscribeLinkNodeKeys.map((key) => editor.getElementByKey(key))
  unsubscribeLinks.forEach((el) => el?.setAttribute("data-unsubscribe-link", "true"))
}

/**
 * lexical-beautiful-mentions stores variables in a way that the resulting HTML has two elements for each variables -
 * one that has the trigger "{{" and another one that has the value e.g. "first name}}".
 * We fix node list in a way to combine two of these into a single text node for each variable.
 */
function fixVariableNodes(nodes: TextNode[]) {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]

    if (node && node.__text === "{{") {
      const nextNode = nodes[i + 1]
      if (nextNode) {
        node.__text += nextNode.__text
        nodes.splice(i + 1, 1)
        ++i
      }
    }
  }
}

/**
 * Lexical adds an extra <br> tag if there are multiple line breaks in a row.
 * This function removes the last <br> tag if there are more than one.
 * Could happen in copy-pasted HTML content since Lexical puts each <br> separately.
 */
function fixLexicalLineBreaks(paragraphs: HTMLCollection) {
  Array.from(paragraphs).forEach((paragraph) => {
    const brTags = paragraph.getElementsByTagName("br")

    const brTagToRemove = brTags.length > 1 ? brTags[brTags.length - 1] : null
    if (!brTagToRemove) return

    paragraph.removeChild(brTagToRemove)
  })
}

function updateLexicalDom(
  initialText: string,
  editor: LexicalEditor,
  root: RootNode,
  makeLinkEditable: MakeLinkEditableCallback,
  enableVariables: boolean,
  insertMention: (options: InsertMention) => boolean
) {
  const parser = new DOMParser()
  const dom = parser.parseFromString(initialText, "text/html")

  // Remove top and bottom text elements, they must be initiated by the `InlineImageNode`
  dom.querySelectorAll('[data-is-top-text="true"]').forEach((e) => e.remove())
  dom.querySelectorAll('[data-is-bottom-text="true"]').forEach((e) => e.remove())

  fixLexicalLineBreaks(dom.body.children)

  const rawParagraphs = Array.from(dom.body.children)

  const unsubscribeLinkNodeKeys: string[] = []

  rawParagraphs.forEach((rawParagraph) => {
    const paragraphDom = parser.parseFromString(rawParagraph.innerHTML, "text/html")
    const nodes = $generateNodesFromDOM(editor, paragraphDom) as TextNode[]

    fixVariableNodes(nodes)

    // Restore paragraph contents inline styling
    nodes.forEach((node, i) => {
      const accordingChild = paragraphDom.body.children[i]
      const style = accordingChild?.getAttribute("style") || ""

      // Restore element inline style
      node.__style = style
      node.__text ||= accordingChild?.textContent || ""

      const shouldApplyInlineStyle = !$isInlineImageNode(node)

      if (shouldApplyInlineStyle) {
        // Restore element formatting for editor state
        style.includes("bold") && !node.hasFormat("bold") && node.toggleFormat("bold")
        style.includes("italic") && !node.hasFormat("italic") && node.toggleFormat("italic")
        style.includes("underline") && !node.hasFormat("underline") && node.toggleFormat("underline")
        style.includes("line-through") && !node.hasFormat("strikethrough") && node.toggleFormat("strikethrough")
      }

      if ($isLinkNode(node)) {
        if (accordingChild?.getAttribute("data-unsubscribe-link") === "true") {
          unsubscribeLinkNodeKeys.push(node.__key)
        }

        requestAnimationFrame(() => {
          makeLinkEditable(node, false)
        })
      }
    })

    // Normally lexical will just ignore all empty nodes unless they are text nodes
    // Sometimes there are non-TextNode nodes that are empty, we need to convert them to text nodes to preserve spacing between elements
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]
      if (!node) continue
      const text = node.__text

      if (text.length !== 0 && !text.trim() && !$isTextNode(node)) {
        const textNode = new TextNode(text)
        nodes.splice(i, 0, textNode)
        i++
      }
    }

    const parentNode = getParentNode(rawParagraph)

    // Add paragraph to editor

    root.append(parentNode.append(...nodes))
  })

  // Post process links and variables after the editor has rendered the appropriate HTML
  requestAnimationFrame(() => {
    fixCustomUnsubscribeLinks(editor, unsubscribeLinkNodeKeys)

    if (enableVariables) {
      insertVariables(root, editor, insertMention)
    }
  })
}

/**
 * I honestly have no idea why the `useRef()` doesn't work for this,
 * but with `useRef()` this value  doesn't change inside of the `updateInitialTextImitateTyping()` loop
 * and doesn't allow breaking the loop early.
 */
let imitateTypingRef = false

// provide this string as `initialText` to clear the input
export const ERASE_CONTENT_DEV_INPUT = "ERASE_CONTENT_DEV_INPUT"

export const InitialTextPlugin = ({
  setIsTyping,
  imitateTyping,
  initialText = "",
  makeLinkEditable,
  enableVariables,
  onBlur,
}: Props) => {
  const [editor] = useLexicalComposerContext()
  const { insertMention } = useBeautifulMentions()
  const { setControls } = useTextEditorControllerContext()

  const resetTextContent = useCallback(
    (textContent: string) => {
      editor.update(() => {
        const root = $getRoot().clear()
        updateLexicalDom(textContent, editor, root, makeLinkEditable, enableVariables, insertMention)
      })
    },
    [editor, enableVariables, insertMention, makeLinkEditable]
  )

  useEffect(
    function setTextEditorControls() {
      setControls({ setTextContent: resetTextContent })
    },
    [resetTextContent, setControls]
  )

  useEffect(() => {
    imitateTypingRef = imitateTyping || false
  }, [imitateTyping])

  const updateInitialTextImitateTyping = React.useCallback(async () => {
    setIsTyping(true)
    // eslint-disable-next-line no-constant-condition
    // If during typing imitation the `imitateTyping` has changed - break the loop

    const text = initialText
    editor.update(() => {
      const root = $getRoot().clear()
      updateLexicalDom(text, editor, root, makeLinkEditable, enableVariables, insertMention)
    })

    setIsTyping(false)
  }, [editor, initialText, makeLinkEditable, enableVariables, setIsTyping, insertMention])

  React.useLayoutEffect(() => {
    if (imitateTyping) {
      updateInitialTextImitateTyping().catch(console.error)
    } else {
      editor.update(() => {
        // We have an early return when `initialText` is empty to keep editor's text when `initialText` is cleaned-up.
        // `ERASE_CONTENT_DEV_INPUT` is used to intentionally clear the editor's content.
        if (initialText === ERASE_CONTENT_DEV_INPUT) {
          $getRoot().clear()
          return
        }
        if (!initialText) return
        const root = $getRoot().clear()
        updateLexicalDom(initialText, editor, root, makeLinkEditable, enableVariables, insertMention)
      })
    }
  }, [
    updateInitialTextImitateTyping,
    initialText,
    imitateTyping,
    editor,
    makeLinkEditable,
    enableVariables,
    insertMention,
  ])

  React.useLayoutEffect(() => {
    return editor.registerCommand(
      BLUR_COMMAND,
      () => {
        if (shouldCallBlurCallback) onBlur?.()

        return true
      },
      3
    )
  }, [editor, onBlur])

  return null
}
